/**
 * Proactive token refresh scheduler
 * Refreshes the access token before it expires so API calls don't hit 401.
 */
import axios from 'axios';
import { useAuthStore } from '@store/auth.store';
import { axiosInstance } from '@core/config/axios.config';

/** Refresh token this many milliseconds before access token expires */
const REFRESH_BEFORE_MS = 5 * 60 * 1000; // 5 minutes

/** Minimum delay before next refresh (avoid thrashing if clock skew) */
const MIN_DELAY_MS = 60 * 1000; // 1 minute

let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Decode JWT payload without verification (client-side only to read exp).
 * Returns exp in milliseconds since epoch, or null if invalid.
 */
function getAccessTokenExpiryMs(accessToken: string): number | null {
    try {
        const parts = accessToken.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = atob(base64);
        const decoded = JSON.parse(json) as { exp?: number };
        if (typeof decoded.exp !== 'number') return null;
        return decoded.exp * 1000;
    } catch {
        return null;
    }
}

/** Delay before retrying refresh after 502/503/504 (ms) */
const RETRY_AFTER_UNAVAILABLE_MS = 3000;

/**
 * Call the refresh endpoint and update store + localStorage.
 * Returns the new access token or null on failure.
 * Retries once after 502/503/504 (e.g. DB timeout) before giving up.
 */
async function doRefresh(retry = true): Promise<string | null> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    const baseURL = axiosInstance.defaults.baseURL || 'http://localhost:8000/api/v1';
    try {
        const response = await axios.post(
            `${baseURL}/auth/refresh`,
            { refresh_token: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
        );
        const newAccessToken = response.data?.access_token || response.data?.accessToken;
        if (!newAccessToken) return null;

        localStorage.setItem('accessToken', newAccessToken);
        const authStore = useAuthStore.getState();
        const currentUser = authStore.user;
        if (currentUser) {
            authStore.setAuth(currentUser, newAccessToken, refreshToken);
        }
        return newAccessToken;
    } catch (e: unknown) {
        const status = axios.isAxiosError(e) ? e.response?.status : undefined;
        const temporary = status === 502 || status === 503 || status === 504;
        if (temporary && retry) {
            await new Promise((r) => setTimeout(r, RETRY_AFTER_UNAVAILABLE_MS));
            return doRefresh(false);
        }
        return null;
    }
}

/**
 * Schedule the next refresh based on current access token expiry.
 * If token is missing/expired or expires soon, refresh immediately.
 */
function scheduleRefresh(): void {
    if (refreshTimeoutId !== null) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
    }

    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    if (!accessToken || !refreshToken) return;

    const expMs = getAccessTokenExpiryMs(accessToken);
    if (!expMs) {
        if (import.meta.env.DEV) {
            console.warn('Token refresh scheduler: could not read token expiry, refreshing soon');
        }
        refreshTimeoutId = setTimeout(() => runRefresh(), MIN_DELAY_MS);
        return;
    }

    const now = Date.now();
    const refreshAt = expMs - REFRESH_BEFORE_MS;
    const delay = refreshAt - now;

    if (delay <= 0) {
        runRefresh();
        return;
    }

    refreshTimeoutId = setTimeout(() => runRefresh(), delay);
    if (import.meta.env.DEV) {
        const mins = Math.round(delay / 60000);
        console.log(`Token refresh scheduled in ${mins} minutes`);
    }
}

async function runRefresh(): Promise<void> {
    refreshTimeoutId = null;
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return;

    try {
        const newToken = await doRefresh();
        if (newToken) {
            scheduleRefresh();
        }
    } catch (e) {
        if (import.meta.env.DEV) {
            console.warn('Proactive token refresh failed:', e);
        }
        // Do not reschedule; next API call will get 401 and interceptor will handle redirect
    }
}

/**
 * Start the token refresh scheduler.
 * Call on app init (when user may already be logged in) and after login/setAuth.
 */
export function startTokenRefreshScheduler(): void {
    scheduleRefresh();
}

/**
 * Stop the token refresh scheduler.
 * Call on logout / clearAuth.
 */
export function stopTokenRefreshScheduler(): void {
    if (refreshTimeoutId !== null) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
    }
}
